import { AnimatePresence, motion } from 'framer-motion';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icons } from '../components/ui/Icons';

type ToastKind = 'success' | 'info' | 'error';
type Toast = { id: number; kind: ToastKind; title: string; body?: string };

export type LeadPrefill = { zip?: string; projectId?: string };

type Ctx = {
  toast: (t: { kind?: ToastKind; title: string; body?: string }) => void;
  openLeadForm: (prefill?: LeadPrefill) => void;
  closeLeadForm: () => void;
  leadOpen: boolean;
  prefill: LeadPrefill;
};

const AppCtx = createContext<Ctx | null>(null);

export function useApp() {
  const c = useContext(AppCtx);
  if (!c) throw new Error('useApp must be used within AppProvider');
  return c;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [leadOpen, setLeadOpen] = useState(false);
  const [prefill, setPrefill] = useState<LeadPrefill>({});
  const idRef = useRef(0);

  const toast = useCallback((t: { kind?: ToastKind; title: string; body?: string }) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, kind: t.kind ?? 'info', title: t.title, body: t.body }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 5200);
  }, []);

  const openLeadForm = useCallback((p?: LeadPrefill) => {
    setPrefill(p ?? {});
    setLeadOpen(true);
  }, []);
  const closeLeadForm = useCallback(() => setLeadOpen(false), []);

  const value = useMemo(
    () => ({ toast, openLeadForm, closeLeadForm, leadOpen, prefill }),
    [toast, openLeadForm, closeLeadForm, leadOpen, prefill],
  );

  return (
    <AppCtx.Provider value={value}>
      {children}
      {/* Custom toast stack — never the browser alert */}
      <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4 sm:top-6">
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: -16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.96 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border border-stone bg-white/95 p-4 shadow-lift backdrop-blur"
              role="status"
            >
              <span
                className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                  t.kind === 'success' ? 'bg-sage/15 text-sage-deep' : t.kind === 'error' ? 'bg-terracotta/15 text-terracotta-deep' : 'bg-plum/10 text-plum'
                }`}
              >
                {t.kind === 'success' ? <Icons.check width={18} height={18} /> : t.kind === 'error' ? <Icons.close width={18} height={18} /> : <Icons.sparkle width={18} height={18} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-body text-sm font-semibold text-plum">{t.title}</p>
                {t.body && <p className="mt-0.5 font-body text-sm text-plum/70">{t.body}</p>}
              </div>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="cursor-pointer rounded-full p-1 text-plum/40 transition-colors hover:text-plum"
                aria-label="Dismiss"
              >
                <Icons.close width={16} height={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </AppCtx.Provider>
  );
}
